import classnames from 'classnames';
import kebabCase from 'lodash/kebabCase';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import mailcheckmark from '../../../assets/images/mailcheckmark.svg';
import smallX from '../../../assets/images/small-x.svg';
import useOnClickOutside from '../../../hooks/useOnClickOutside';
import Separator from '../Separator/Separator';
import Spinner from '../Spinner/Spinner';
import styles from './Combobox.module.scss';

interface ComboboxProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'style' | 'onSelect' | 'children'
  > {
  dropdownClassName?: string;
  icon?: string;
  onSelect?: (value?: string) => void;
  options: ComboboxOption[];
  children: (option: ComboboxOption) => JSX.Element;
  loading?: boolean;
  error?: string;
  label?: string;
}

interface ComboboxOption {
  label: string;
  value: string;
  description?: string;
}

const Combobox = ({
  label,
  className,
  required,
  error,
  options,
  disabled,
  placeholder,
  icon,
  onSelect,
  onChange,
  loading,
  value,
  dropdownClassName,
  children,
  ...rest
}: ComboboxProps) => {
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');

  const ref = useRef(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const handleClickOutside = () => {
    setSearch('');
    setShowDropdown(false);
  };

  const selectedOption = useMemo(
    () => options?.find((option) => option.value === value),
    [value, options],
  );

  const renderedPlaceholder = selectedOption ? '' : placeholder;

  useOnClickOutside(ref, handleClickOutside);

  useEffect(() => {
    if (showDropdown) {
      inputRef.current?.focus();
    }
  }, [showDropdown]);

  return (
    <div className={`relative ${className}`}>
      <p className='font-medium text-sm text-gray-800'>
        {label}
        {required && '*'}
      </p>
      <div
        className='mobile:mb-2 tablet:mb-6 w-full flex flex-col relative'
        onClick={() => {
          setShowDropdown(true);
        }}
      >
        <div
          className={` text-gray-500 flex items-center rounded-md outline-none mobile:mt-2 w-full ${
            styles.input_wrapper
          } py-2 px-3 ${error && 'border-red-400'} ${
            disabled && 'bg-gray-200'
          }`}
        >
          {icon && <img className='mr-2 w-5' src={icon} alt={label} />}
          {!showDropdown && selectedOption && (
            <div role='option' aria-selected>
              {children(selectedOption)}
            </div>
          )}
          <input
            ref={inputRef}
            value={search}
            type='text'
            className={classnames('outline-none flex-1', {
              invisible: !showDropdown && selectedOption,
              'bg-gray-200': disabled,
            })}
            placeholder={renderedPlaceholder}
            onChange={(e) => {
              setSearch(e.target.value);
              onChange?.(e);
            }}
            readOnly={disabled}
            disabled={disabled}
            {...rest}
          />
          {selectedOption && !loading && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                onSelect?.(undefined);
              }}
            >
              <img
                className='ml-2 w-3 cursor-pointer'
                src={smallX}
                alt='Remove selection.'
              />
            </span>
          )}
          {loading && <Spinner variant='blue' />}
          {error && (
            <span
              style={{ bottom: '-20px', right: 0 }}
              className='absolute text-xs text-red-400'
            >
              {error}
            </span>
          )}
        </div>
      </div>
      {showDropdown && (
        <div
          ref={ref}
          className={`rounded-md absolute z-10 w-full  bg-white overflow-auto ${styles.dropdown_placement} ${dropdownClassName}`}
          role='group'
        >
          {options
            ?.filter((option) =>
              `${option.label}__${option.description}`
                .toLowerCase()
                .includes(search.toLowerCase() as string),
            )
            .map((option, index) => (
              <>
                <div
                  key={`${kebabCase(option.label)}-${option.value}`}
                  className='p-3 cursor-pointer hover:bg-gray-100 flex justify-between'
                  onClick={() => {
                    onSelect?.(option.value);
                    setSearch('');
                    setShowDropdown(false);
                  }}
                  role='option'
                  aria-selected={value === option.value}
                  tabIndex={index === 0 ? 0 : -1}
                >
                  <div className='flex mobile:flex-col tablet:flex-row'>
                    <p className='not-italic font-semibold text-sm leading-5 text-gray-900 mr-2'>
                      {option.label}
                    </p>
                    <p className='not-italic font-normal text-sm leading-5 text-gray-600'>
                      {option.description}
                    </p>
                  </div>
                  {value === option.value && (
                    <img
                      src={mailcheckmark}
                      alt='mail checkmark'
                      className='mr-3'
                    />
                  )}
                </div>
                <Separator />
              </>
            ))}
        </div>
      )}
    </div>
  );
};

export default Combobox;
